import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { ScoreThreshold, ExScoreThreshold, ExScoreDisplayMode, DetailMainDisplay } from '../types/scoreThreshold';
import { DEFAULT_SCORE_THRESHOLDS, MAX_SCORE_THRESHOLDS, DEFAULT_EXSCORE_THRESHOLDS, MAX_EXSCORE_THRESHOLDS } from '../types/scoreThreshold';
import { useAuth } from './AuthContext';
import { apiService } from '../services/api';

interface ScoreThresholdContextType {
  thresholds: ScoreThreshold[];
  enabledThresholds: ScoreThreshold[];
  addThreshold: (threshold: Omit<ScoreThreshold, 'id' | 'isDefault'>) => boolean;
  removeThreshold: (id: string) => void;
  updateThreshold: (id: string, updates: Partial<ScoreThreshold>) => void;
  toggleThreshold: (id: string) => void;
  resetToDefaults: () => void;
  canAddMore: boolean;
  isLoading: boolean;
  isSaving: boolean;
  // EX-SCORE thresholds
  exScoreThresholds: ExScoreThreshold[];
  enabledExScoreThresholds: ExScoreThreshold[];
  addExScoreThreshold: (threshold: Omit<ExScoreThreshold, 'id' | 'isDefault'>) => boolean;
  removeExScoreThreshold: (id: string) => void;
  toggleExScoreThreshold: (id: string) => void;
  resetExScoreToDefaults: () => void;
  canAddMoreExScore: boolean;
  // EX-SCORE display mode
  exScoreDisplayMode: ExScoreDisplayMode;
  setExScoreDisplayMode: (mode: ExScoreDisplayMode) => void;
  formatExScore: (exScore: number, maxExScore: number) => string;
  // Detail main display (aggregate EX display)
  detailMainDisplay: DetailMainDisplay;
  setDetailMainDisplay: (mode: DetailMainDisplay) => Promise<void>;
  formatExAvg: (percent: number | null | undefined, gap: number | null | undefined) => string;
}

const ScoreThresholdContext = createContext<ScoreThresholdContextType | undefined>(undefined);

export const ScoreThresholdProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [thresholds, setThresholds] = useState<ScoreThreshold[]>(DEFAULT_SCORE_THRESHOLDS);
  const [exScoreThresholds, setExScoreThresholds] = useState<ExScoreThreshold[]>(DEFAULT_EXSCORE_THRESHOLDS);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isExScoreInitialized, setIsExScoreInitialized] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const saveExScoreTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track the last fetched value to prevent saving on initial load
  const lastFetchedThresholdsRef = useRef<string | null>(null);
  const lastFetchedExScoreThresholdsRef = useRef<string | null>(null);

  // EX-SCORE display mode (서버에 저장)
  const [exScoreDisplayMode, setExScoreDisplayModeState] = useState<ExScoreDisplayMode>('percent');
  const [isExScoreDisplayModeInitialized, setIsExScoreDisplayModeInitialized] = useState(false);

  const setExScoreDisplayMode = useCallback(async (mode: ExScoreDisplayMode) => {
    setExScoreDisplayModeState(mode);
    // 서버에 저장
    try {
      const format = mode === 'maxMinus' ? 'MAX_MINUS' : 'PERCENT';
      await apiService.updateExScoreDisplayFormat(format);
    } catch (e) {
      console.error('Failed to save exScoreDisplayMode:', e);
    }
  }, []);

  // Detail main display (집계 EX 표시 기준, 서버에 저장)
  const [detailMainDisplay, setDetailMainDisplayState] = useState<DetailMainDisplay>('maxMinus');

  const setDetailMainDisplay = useCallback(async (mode: DetailMainDisplay) => {
    setDetailMainDisplayState(mode);
    try {
      const format = mode === 'maxMinus' ? 'MAX_MINUS' : 'PERCENT';
      await apiService.updateDetailMainDisplay(format);
    } catch (e) {
      console.error('Failed to save detailMainDisplay:', e);
    }
  }, []);

  // 집계 EX 평균 포맷 함수 (detailMainDisplay 기반)
  const formatExAvg = useCallback((percent: number | null | undefined, gap: number | null | undefined): string => {
    if (percent == null) return '—';
    if (percent >= 100) return 'MAX';
    if (detailMainDisplay === 'maxMinus' && gap != null) {
      const r = Math.round(gap * 10) / 10;
      return r <= 0 ? 'MAX' : `MAX-${r.toFixed(1)}`;
    }
    const truncated = Math.floor(percent * 10) / 10;
    return `${truncated.toFixed(1)}%`;
  }, [detailMainDisplay]);

  // EX-SCORE 포맷 함수: 98%를 넘는 경우에만 표시 모드에 따라 퍼센트 또는 MAX-X 형식으로 반환
  const formatExScore = useCallback((exScore: number, maxExScore: number): string => {
    if (maxExScore <= 0) return '0%';

    const percent = (exScore / maxExScore) * 100;
    const truncated1 = Math.floor(percent * 10) / 10;

    // 98% 이하는 항상 퍼센트로 표시
    if (percent < 98) {
      return `${truncated1.toFixed(1)}%`;
    }

    // 98% 초과일 때만 설정에 따라 표시
    if (exScoreDisplayMode === 'maxMinus') {
      const diff = maxExScore - exScore;
      return `MAX-${diff}`;
    } else {
      return `${truncated1.toFixed(1)}%`;
    }
  }, [exScoreDisplayMode]);

  // Fetch thresholds from server when user is authenticated
  useEffect(() => {
    const fetchThresholds = async () => {
      if (!isAuthenticated) {
        setThresholds(DEFAULT_SCORE_THRESHOLDS);
        setExScoreThresholds(DEFAULT_EXSCORE_THRESHOLDS);
        setExScoreDisplayModeState('percent'); // 비로그인 시 기본값
        lastFetchedThresholdsRef.current = JSON.stringify(DEFAULT_SCORE_THRESHOLDS);
        lastFetchedExScoreThresholdsRef.current = JSON.stringify(DEFAULT_EXSCORE_THRESHOLDS);
        setIsInitialized(true);
        setIsExScoreInitialized(true);
        setIsExScoreDisplayModeInitialized(true);
        return;
      }

      setIsLoading(true);
      try {
        const profile = await apiService.getProfile();
        // Score thresholds
        if (profile.scoreThresholds && profile.scoreThresholds.length > 0) {
          setThresholds(profile.scoreThresholds);
          lastFetchedThresholdsRef.current = JSON.stringify(profile.scoreThresholds);
        } else {
          setThresholds(DEFAULT_SCORE_THRESHOLDS);
          lastFetchedThresholdsRef.current = JSON.stringify(DEFAULT_SCORE_THRESHOLDS);
        }
        // EX-SCORE thresholds
        if (profile.exScoreThresholds && profile.exScoreThresholds.length > 0) {
          setExScoreThresholds(profile.exScoreThresholds);
          lastFetchedExScoreThresholdsRef.current = JSON.stringify(profile.exScoreThresholds);
        } else {
          setExScoreThresholds(DEFAULT_EXSCORE_THRESHOLDS);
          lastFetchedExScoreThresholdsRef.current = JSON.stringify(DEFAULT_EXSCORE_THRESHOLDS);
        }
        // EX-SCORE display format (서버에서 로드)
        const serverFormat = profile.exScoreDisplayFormat;
        const displayMode = serverFormat === 'MAX_MINUS' ? 'maxMinus' : 'percent';
        setExScoreDisplayModeState(displayMode);
        setIsExScoreDisplayModeInitialized(true);
        // Detail main display (집계 EX 표시 기준, 서버에서 로드)
        if (profile.detailMainDisplay) {
          setDetailMainDisplayState(profile.detailMainDisplay === 'MAX_MINUS' ? 'maxMinus' : 'percent');
        }
      } catch (e) {
        console.error('Failed to fetch thresholds:', e);
        // On error, use default but don't save to server
        setThresholds(DEFAULT_SCORE_THRESHOLDS);
        setExScoreThresholds(DEFAULT_EXSCORE_THRESHOLDS);
        lastFetchedThresholdsRef.current = JSON.stringify(DEFAULT_SCORE_THRESHOLDS);
        lastFetchedExScoreThresholdsRef.current = JSON.stringify(DEFAULT_EXSCORE_THRESHOLDS);
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
        setIsExScoreInitialized(true);
      }
    };

    fetchThresholds();
  }, [isAuthenticated]);

  // Debounced save to server when thresholds change
  useEffect(() => {
    if (!isAuthenticated || !isInitialized) return;

    // Skip saving if thresholds haven't actually changed from what was fetched
    const currentThresholdsJson = JSON.stringify(thresholds);
    if (lastFetchedThresholdsRef.current === currentThresholdsJson) {
      return;
    }

    // Clear previous timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce save (500ms)
    saveTimeoutRef.current = setTimeout(async () => {
      setIsSaving(true);
      try {
        await apiService.updateScoreThresholds(thresholds);
        // Update the last fetched ref after successful save
        lastFetchedThresholdsRef.current = currentThresholdsJson;
      } catch (e) {
        console.error('Failed to save score thresholds:', e);
      } finally {
        setIsSaving(false);
      }
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [thresholds, isAuthenticated, isInitialized]);

  // Debounced save to server when exScoreThresholds change
  useEffect(() => {
    if (!isAuthenticated || !isExScoreInitialized) return;

    // Skip saving if exScoreThresholds haven't actually changed from what was fetched
    const currentExScoreThresholdsJson = JSON.stringify(exScoreThresholds);
    if (lastFetchedExScoreThresholdsRef.current === currentExScoreThresholdsJson) {
      return;
    }

    // Clear previous timeout
    if (saveExScoreTimeoutRef.current) {
      clearTimeout(saveExScoreTimeoutRef.current);
    }

    // Debounce save (500ms)
    saveExScoreTimeoutRef.current = setTimeout(async () => {
      setIsSaving(true);
      try {
        await apiService.updateExScoreThresholds(exScoreThresholds);
        // Update the last fetched ref after successful save
        lastFetchedExScoreThresholdsRef.current = currentExScoreThresholdsJson;
      } catch (e) {
        console.error('Failed to save EX-SCORE thresholds:', e);
      } finally {
        setIsSaving(false);
      }
    }, 500);

    return () => {
      if (saveExScoreTimeoutRef.current) {
        clearTimeout(saveExScoreTimeoutRef.current);
      }
    };
  }, [exScoreThresholds, isAuthenticated, isExScoreInitialized]);

  const enabledThresholds = thresholds.filter(t => t.enabled);

  const canAddMore = thresholds.length < MAX_SCORE_THRESHOLDS;

  const addThreshold = useCallback((threshold: Omit<ScoreThreshold, 'id' | 'isDefault'>): boolean => {
    if (thresholds.length >= MAX_SCORE_THRESHOLDS) return false;

    const newThreshold: ScoreThreshold = {
      ...threshold,
      id: `custom_${Date.now()}`,
      isDefault: false,
    };

    setThresholds(prev => [...prev, newThreshold]);
    return true;
  }, [thresholds.length]);

  const removeThreshold = useCallback((id: string) => {
    setThresholds(prev => prev.filter(t => t.id !== id || t.isDefault));
  }, []);

  const updateThreshold = useCallback((id: string, updates: Partial<ScoreThreshold>) => {
    setThresholds(prev => prev.map(t =>
      t.id === id ? { ...t, ...updates } : t
    ));
  }, []);

  const toggleThreshold = useCallback((id: string) => {
    setThresholds(prev => prev.map(t =>
      t.id === id ? { ...t, enabled: !t.enabled } : t
    ));
  }, []);

  const resetToDefaults = useCallback(() => {
    setThresholds(DEFAULT_SCORE_THRESHOLDS);
  }, []);

  // EX-SCORE thresholds
  const enabledExScoreThresholds = exScoreThresholds.filter(t => t.enabled);
  const canAddMoreExScore = exScoreThresholds.length < MAX_EXSCORE_THRESHOLDS;

  const addExScoreThreshold = useCallback((threshold: Omit<ExScoreThreshold, 'id' | 'isDefault'>): boolean => {
    if (exScoreThresholds.length >= MAX_EXSCORE_THRESHOLDS) return false;

    const newThreshold: ExScoreThreshold = {
      ...threshold,
      id: `ex_custom_${Date.now()}`,
      isDefault: false,
    };

    setExScoreThresholds(prev => [...prev, newThreshold]);
    return true;
  }, [exScoreThresholds.length]);

  const removeExScoreThreshold = useCallback((id: string) => {
    setExScoreThresholds(prev => prev.filter(t => t.id !== id || t.isDefault));
  }, []);

  const toggleExScoreThreshold = useCallback((id: string) => {
    setExScoreThresholds(prev => prev.map(t =>
      t.id === id ? { ...t, enabled: !t.enabled } : t
    ));
  }, []);

  const resetExScoreToDefaults = useCallback(() => {
    setExScoreThresholds(DEFAULT_EXSCORE_THRESHOLDS);
  }, []);

  return (
    <ScoreThresholdContext.Provider value={{
      thresholds,
      enabledThresholds,
      addThreshold,
      removeThreshold,
      updateThreshold,
      toggleThreshold,
      resetToDefaults,
      canAddMore,
      isLoading,
      isSaving,
      // EX-SCORE
      exScoreThresholds,
      enabledExScoreThresholds,
      addExScoreThreshold,
      removeExScoreThreshold,
      toggleExScoreThreshold,
      resetExScoreToDefaults,
      canAddMoreExScore,
      // EX-SCORE display mode
      exScoreDisplayMode,
      setExScoreDisplayMode,
      formatExScore,
      // Detail main display
      detailMainDisplay,
      setDetailMainDisplay,
      formatExAvg,
    }}>
      {children}
    </ScoreThresholdContext.Provider>
  );
};

export const useScoreThreshold = () => {
  const context = useContext(ScoreThresholdContext);
  if (!context) {
    throw new Error('useScoreThreshold must be used within a ScoreThresholdProvider');
  }
  return context;
};
